<script setup>
import { computed, reactive, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import { useStore } from 'dashboard/composables/store';
import { useStoreGetters } from 'dashboard/composables/store';
import CompanyAPI from 'dashboard/api/companies';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
  initialCompanyId: {
    type: Number,
    default: null,
  },
  initialCompanyDomain: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const contactFieldDefs = computed(() =>
  getters['contactFieldDefinitions/getEnabled'].value
);

onMounted(() => store.dispatch('contactFieldDefinitions/fetchAll'));

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  companyId: null,
  companyDomain: '',
  extraFields: {},
  additionalAttributes: {
    description: '',
    countryCode: '',
    country: '',
    city: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    // Pre-fill company from parent context (e.g. creating from Company page)
    if (props.initialCompanyId) {
      state.companyId = props.initialCompanyId;
    }
    return;
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    additionalAttributes = {},
    extraFields: extraFieldsRaw = {},
    companyId: companyIdRaw = null,
    companyDomain: companyDomainRaw = '',
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    countryCode = '',
    country = '',
    city = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    companyId: companyIdRaw,
    companyDomain: companyDomainRaw,
    extraFields: { ...(extraFieldsRaw || {}) },
    additionalAttributes: {
      description,
      countryCode,
      country,
      city,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      const isFormValid = await v$.value.$validate();
      if (isFormValid) {
        const { firstName, lastName, ...stateWithoutNames } = state;
        emit('update', stateWithoutNames);
      }
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, { ...defaultState, extraFields: {}, companyId: null, companyDomain: '' });
  groupSearchQuery.value = {};
  groupSearchResults.value = {};
  groupDropdownOpen.value = {};
};

const updateExtraField = (key, value) => {
  state.extraFields = { ...state.extraFields, [key]: value };
  emit('update', state);
};

// ── Group field (company lookup) ──────────────────────────────────────────────
const groupSearchQuery   = ref({});
const groupSearchResults = ref({});
const groupDropdownOpen  = ref({});

const getGroupDisplayValue = fieldKey => {
  if (!state.companyId) return groupSearchQuery.value[fieldKey] || '';
  const match = groupSearchResults.value[fieldKey]?.find(c => c.id === state.companyId);
  if (match) return match.domain || match.name || '';
  return props.initialCompanyDomain || state.companyDomain || groupSearchQuery.value[fieldKey] || '';
};

let groupSearchTimer = null;
const onGroupSearch = (fieldKey, query) => {
  groupSearchQuery.value = { ...groupSearchQuery.value, [fieldKey]: query };
  if (!query.trim()) {
    groupSearchResults.value = { ...groupSearchResults.value, [fieldKey]: [] };
    return;
  }
  clearTimeout(groupSearchTimer);
  groupSearchTimer = setTimeout(async () => {
    try {
      const { data } = await CompanyAPI.search(query);
      groupSearchResults.value = {
        ...groupSearchResults.value,
        [fieldKey]: (data.payload || []).slice(0, 10),
      };
    } catch { /* ignore */ }
  }, 250);
};

const selectGroupCompany = (fieldKey, company) => {
  state.companyId = company.id;
  state.companyDomain = company.domain || company.name || '';
  groupSearchQuery.value = { ...groupSearchQuery.value, [fieldKey]: company.domain || company.name || '' };
  groupSearchResults.value = { ...groupSearchResults.value, [fieldKey]: [] };
  groupDropdownOpen.value = { ...groupDropdownOpen.value, [fieldKey]: false };
  emit('update', state);
};

const clearGroupCompany = fieldKey => {
  state.companyId = null;
  state.companyDomain = '';
  groupSearchQuery.value = { ...groupSearchQuery.value, [fieldKey]: '' };
  groupSearchResults.value = { ...groupSearchResults.value, [fieldKey]: [] };
  emit('update', state);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Sync extra fields when parent refreshes the contact from the API
watch(
  () => props.contactData?.extraFields,
  newExtraFields => {
    if (newExtraFields !== undefined) {
      state.extraFields = { ...(newExtraFields || {}) };
    }
  },
  { immediate: true }
);

// Sync company assignment when parent refreshes after save
watch(
  () => props.contactData?.companyId,
  newCompanyId => {
    if (newCompanyId !== undefined && newCompanyId !== state.companyId) {
      state.companyId = newCompanyId ?? null;
    }
  },
  { immediate: true }
);

watch(
  () => props.contactData?.companyDomain,
  newCompanyDomain => {
    if (newCompanyDomain !== undefined && newCompanyDomain !== state.companyDomain) {
      state.companyDomain = newCompanyDomain ?? '';
    }
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <ComboBox
            v-if="item.key === 'COUNTRY'"
            v-model="state.additionalAttributes.countryCode"
            :options="countryOptions"
            :placeholder="item.placeholder"
            class="[&>div>button]:h-8"
            :class="{
              '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                !isDetailsView,
              '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
            }"
            @update:model-value="handleCountrySelection"
          />
          <PhoneNumberInput
            v-else-if="item.key === 'PHONE_NUMBER'"
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :show-border="isDetailsView"
          />
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :message-type="getMessageType(item.key)"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
          />
        </template>
        <!-- Custom contact fields (from Contact Fields Settings) -->
        <template v-for="field in contactFieldDefs" :key="field.id">
          <!-- List type → dropdown -->
          <div v-if="field.field_type === 'list'" class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-10">{{ field.label }}</label>
            <select
              :value="state.extraFields[field.field_key] || ''"
              class="w-full h-8 px-2 text-sm rounded-lg border border-n-weak dark:border-n-weak bg-transparent dark:bg-slate-800 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              @change="updateExtraField(field.field_key, $event.target.value)"
            >
              <option value="">— Select —</option>
              <option v-for="opt in (field.options || [])" :key="opt" :value="opt">{{ opt }}</option>
            </select>
          </div>
          <!-- Checkbox type -->
          <div v-else-if="field.field_type === 'checkbox'" class="flex items-center gap-3 h-8">
            <input
              type="checkbox"
              :checked="!!state.extraFields[field.field_key]"
              class="size-4 rounded accent-woot-500 cursor-pointer"
              @change="updateExtraField(field.field_key, $event.target.checked)"
            />
            <label class="text-sm text-n-slate-12 cursor-pointer">{{ field.label }}</label>
          </div>
          <!-- Date type -->
          <Input
            v-else-if="field.field_type === 'date'"
            :model-value="state.extraFields[field.field_key] || ''"
            :placeholder="field.label"
            type="date"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${!isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''}`"
            class="w-full"
            @update:model-value="updateExtraField(field.field_key, $event)"
          />
          <!-- Group type → company domain search -->
          <div v-else-if="field.field_type === 'group'" class="flex flex-col gap-1 relative">
            <label class="text-xs text-n-slate-10">{{ field.label }}</label>
            <div class="relative flex items-center">
              <span class="i-lucide-building-2 absolute left-2 size-3.5 text-n-slate-8 pointer-events-none" />
              <input
                :value="getGroupDisplayValue(field.field_key)"
                type="text"
                :placeholder="field.label + '…'"
                class="w-full h-8 pl-7 pr-7 text-sm rounded-lg border border-n-weak bg-transparent text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                @input="onGroupSearch(field.field_key, $event.target.value)"
                @focus="groupDropdownOpen = { ...groupDropdownOpen, [field.field_key]: true }"
              />
              <button
                v-if="state.companyId"
                class="absolute right-2 text-n-slate-8 hover:text-red-500"
                type="button"
                @click="clearGroupCompany(field.field_key)"
              >
                <span class="i-lucide-x size-3.5" />
              </button>
            </div>
            <div
              v-if="groupDropdownOpen[field.field_key] && groupSearchResults[field.field_key]?.length"
              class="absolute top-full left-0 right-0 z-50 mt-1 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg overflow-hidden"
            >
              <button
                v-for="c in groupSearchResults[field.field_key]"
                :key="c.id"
                type="button"
                class="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-n-alpha-2 transition-colors"
                @mousedown.prevent="selectGroupCompany(field.field_key, c)"
              >
                <span class="i-lucide-building-2 size-3.5 text-n-slate-8 shrink-0" />
                <div class="min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 truncate">{{ c.name }}</p>
                  <p v-if="c.domain" class="text-xs text-n-slate-9 truncate">{{ c.domain }}</p>
                </div>
              </button>
            </div>
          </div>
          <!-- Text / Number / Link -->
          <Input
            v-else
            :model-value="state.extraFields[field.field_key] || ''"
            :placeholder="field.label"
            :type="field.field_type === 'number' ? 'number' : field.field_type === 'link' ? 'url' : 'text'"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${!isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''}`"
            class="w-full"
            @update:model-value="updateExtraField(field.field_key, $event)"
          />
        </template>
      </div>
    </div>

    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
